// First-launch onboarding — the five pages of TrailBivvy/Features/Onboarding/OnboardingScreen.swift with the same
// artwork. Points that are iOS-specific (the iPhone, the iOS location prompt) use web wording (pwa.* strings). The
// browser asks for the position only when a feature needs it, never from the onboarding (decisions.md PWA-25).

import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n';
import { Icon, type IconName } from '../components/Icon';
import { useEnv } from '../env';
import welcome from './img/welcome.jpg';
import honesty from './img/honesty.jpg';
import offline from './img/offline.jpg';
import privacy from './img/privacy.jpg';
import safety from './img/safety.jpg';

type Step = 'welcome' | 'honesty' | 'offline' | 'privacy' | 'safety';
const steps: Step[] = ['welcome', 'honesty', 'offline', 'privacy', 'safety'];
const art: Record<Step, string> = { welcome, honesty, offline, privacy, safety };

function page(step: Step): { title: string; subtitle: string; points: Array<[IconName, string, string]> } {
  switch (step) {
    case 'welcome':
      return {
        title: t('onboarding.welcome.title'),
        subtitle: t('onboarding.welcome.subtitle.v2'),
        points: [
          ['map', t('onboarding.welcome.map.title'), t('onboarding.welcome.map.detail')],
          ['moon', t('common.sleepTonight'), t('onboarding.welcome.tonight.detail.v2')],
        ],
      };
    case 'honesty':
      return {
        title: t('onboarding.honesty.title'),
        subtitle: t('onboarding.honesty.subtitle'),
        points: [
          ['mountain', t('onboarding.honesty.terrain.title'), t('onboarding.honesty.terrain.detail.v2')],
          ['doc', t('onboarding.honesty.rules.title'), t('onboarding.honesty.rules.detail')],
          ['question', t('onboarding.honesty.unknown.title'), t('onboarding.honesty.unknown.detail')],
        ],
      };
    case 'offline':
      return {
        title: t('onboarding.offline.title'),
        subtitle: t('onboarding.offline.subtitle.v2'),
        points: [
          ['download', t('onboarding.offline.download.title'), t('pwa.onboarding.offline.download.detail')],
          ['wifi', t('onboarding.offline.internet.title'), t('onboarding.offline.internet.detail')],
          ['clock', t('onboarding.offline.dated.title'), t('onboarding.offline.dated.detail')],
        ],
      };
    case 'privacy':
      return {
        title: t('onboarding.privacy.title'),
        subtitle: t('onboarding.privacy.subtitle'),
        points: [
          ['location', t('onboarding.privacy.location.title'), t('pwa.onboarding.privacy.location.detail')],
          ['lock', t('onboarding.privacy.device.title'), t('pwa.onboarding.privacy.device.detail')],
        ],
      };
    case 'safety':
      return {
        title: t('onboarding.safety.title'),
        subtitle: t('onboarding.safety.subtitle'),
        points: [
          ['thunder', t('onboarding.safety.weather.title'), t('onboarding.safety.weather.detail')],
          ['safety', t('onboarding.safety.button.title'), t('onboarding.safety.button.detail.v2')],
          ['leaf', t('onboarding.safety.trace.title'), t('onboarding.safety.trace.detail')],
        ],
      };
  }
}

export function OnboardingScreen() {
  const env = useEnv();
  const [index, setIndex] = useState(0);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const step = steps[index];
  const content = page(step);
  const last = index === steps.length - 1;

  useEffect(() => {
    titleRef.current?.focus();
  }, [index]);

  const finish = () => {
    env.prefs.onboardingCompleted.value = true;
  };

  return (
    <div class="onboarding" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
      <div class="onboarding-art" style={{ backgroundImage: `url(${art[step]})` }} aria-hidden="true" />
      <div class="onboarding-panel">
        <div class="inline" style={{ justifyContent: 'space-between' }}>
          {index > 0 ? (
            <button type="button" class="btn btn-plain" onClick={() => setIndex(index - 1)}>
              {t('onboarding.back')}
            </button>
          ) : (
            <span />
          )}
          {!last && (
            <button type="button" class="btn btn-plain" onClick={() => setIndex(steps.length - 1)} title={t('onboarding.skip.hint')}>
              {t('onboarding.skip')}
            </button>
          )}
        </div>
        <h1 id="onboarding-title" tabIndex={-1} ref={titleRef}>
          {content.title}
        </h1>
        <p class="body" style={{ opacity: 0.9 }}>
          {content.subtitle}
        </p>
        <ul class="value-list">
          {content.points.map(([icon, title, detail]) => (
            <li key={title}>
              <Icon name={icon} />
              <div>
                <p class="headline">{title}</p>
                <p class="footnote" style={{ color: 'rgba(255,255,255,0.82)' }}>
                  {detail}
                </p>
              </div>
            </li>
          ))}
        </ul>
        <div class="onboarding-dots" aria-label={t('onboarding.pageOf', String(index + 1), String(steps.length))}>
          {steps.map((s, i) => (
            <span key={s} aria-current={i === index ? 'step' : undefined} />
          ))}
        </div>
        <button type="button" class="btn btn-primary" onClick={last ? finish : () => setIndex(index + 1)}>
          {last ? t('onboarding.getStarted') : t('onboarding.next')}
        </button>
      </div>
    </div>
  );
}
